import { useEffect, useRef } from "react";
import { BookOpen, GraduationCap, ShieldCheck, Briefcase } from "lucide-react";

const ICONS = [BookOpen, GraduationCap, ShieldCheck, Briefcase];

export default function AboutSection() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add("visible"); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" data-testid="about-section" ref={ref} className="reveal-section py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left */}
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">
              About Us
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-foreground mb-6">
              About Open Sky Adventure Academy
            </h2>
            <div className="space-y-5">
              <p className="text-base leading-relaxed text-muted-foreground font-light">
                We believe that the most profound lessons are not found in textbooks-they are
                discovered in the soil beneath a student's hands, felt in the wind above a sailboat,
                and understood in the engineering of a real aircraft.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground font-light">
                Open Sky Adventure Academy is an experiential learning institution purpose-built
                to bridge the gap between academic knowledge and real-world application. Our 7.5-acre
                campus serves as a bridge between theoretical academic concepts and tangible, sensory experience.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground font-light">
                With over a decade of insight into how India's students learn most effectively,
                every program is rooted in pedagogy, powered by experience, and aligned to the
                evolving vision of education in India under <span className="font-medium text-foreground">NEP 2020</span>.
              </p>
            </div>
          </div>

          {/* Right - Stats Grid */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { title: "NEP 2020 Aligned", desc: "Programs designed in alignment with India's National Education Policy" },
              { title: "Curriculum-Mapped", desc: "CBSE, ICSE, IGCSE, IB & Karnataka State Board" },
              { title: "Fully Supervised", desc: "Device-free campus with 1:20 staff-to-student ratio" },
              { title: "Turnkey Service", desc: "Transport, meals, safety, insurance & post-visit reports included" },
            ].map((item, i) => {
              const Icon = ICONS[i];
              return (
              <div
                key={i}
                className="p-6 border border-border/60 bg-card hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h4 className="font-heading text-lg font-medium text-foreground mb-2">{item.title}</h4>
                <p className="text-sm text-muted-foreground font-light leading-relaxed">{item.desc}</p>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
